import type {
  LineInfo,
  Reading,
  StressMark,
  WordInfo,
} from "@/lib/tool-kit/poetry/pronounce";

/**
 * Scansion from dictionary stress: which metre fits a poem, how closely each line fits it,
 * and where it departs.
 *
 * The method, in the open so a reader can disagree with it:
 *
 * 1. Each syllable gets a **class** from the CMU stress digit. A stressed syllable of a
 *    word with two or more syllables is `S`; an unstressed one is `w`; a secondary stress
 *    is `x`. A **one-syllable word** is `f` (flexible) when it is a function word — the
 *    dictionary writes "to" as stressed and "the" as unstressed, but speakers put either
 *    on or off the beat — and `M` (a stressed content word) otherwise, which verse may
 *    demote. Syllables of words the dictionary lacks are `u` (unknown).
 * 2. Each candidate metre (iambic, trochaic, anapestic or dactylic, one to eight feet) is
 *    a template of strong and weak slots, with the variations that do not change the
 *    metre: a feminine ending, a catalectic (cut-short) line, a headless start.
 * 3. A line is **aligned** to a template by dynamic programming. A syllable in a slot it
 *    suits costs nothing; a mismatch costs a fraction or a whole point (see `COST`); an
 *    extra or missing syllable costs 0.6–0.8.
 * 4. The **dominant metre** is the candidate with the lowest cost per syllable over the
 *    whole poem. A line's **fit** is 1 − cost ÷ syllables.
 */

export type FootId = "iamb" | "trochee" | "anapest" | "dactyl";

export const FOOT_IDS: readonly FootId[] = [
  "iamb",
  "trochee",
  "anapest",
  "dactyl",
];

export const MAX_FEET = 8;

/** The longest line tried automatically: eight two-syllable feet or six of three. */
export function maxFeetFor(foot: FootId): number {
  return FOOT_PATTERN[foot].length === 3 ? 6 : MAX_FEET;
}

export type SylClass = "S" | "M" | "w" | "f" | "x" | "u";

export interface Syl {
  wordIndex: number;
  cls: SylClass;
  digit: StressMark;
}

/** Syllables of a line under one reading per word. */
export function syllablesOf(
  words: readonly WordInfo[],
  readings: readonly Reading[]
): Syl[] {
  const out: Syl[] = [];
  for (const [wordIndex, word] of words.entries()) {
    const reading = readings[wordIndex];
    for (const digit of reading.stress) {
      out.push({ wordIndex, cls: classify(word, reading, digit), digit });
    }
  }
  return out;
}

function classify(
  word: WordInfo,
  reading: Reading,
  digit: StressMark
): SylClass {
  if (digit === null) {
    return "u";
  }
  if (reading.syllables === 1) {
    return word.functionWord || digit === 0 ? "f" : "M";
  }
  if (digit === 1) {
    return "S";
  }
  return digit === 2 ? "x" : "w";
}

/** Cost of putting a syllable class in a strong or weak slot. */
const COST: Record<SylClass, { strong: number; weak: number }> = {
  S: { strong: 0, weak: 1 },
  M: { strong: 0, weak: 0.5 },
  w: { strong: 1, weak: 0 },
  f: { strong: 0.25, weak: 0 },
  x: { strong: 0.25, weak: 0.25 },
  u: { strong: 0.25, weak: 0.25 },
};

const SKIP_WEAK_SYLLABLE = 0.6;
const SKIP_OTHER_SYLLABLE = 1;
const SKIP_SLOT = 0.8;

/* -------------------------------------------------------------- templates */

export type Variant =
  | "regular"
  | "feminine"
  | "catalectic"
  | "headless"
  | "truncated";

export interface Template {
  foot: FootId;
  feet: number;
  variant: Variant;
  /** True for a strong slot. */
  slots: boolean[];
  /** The foot (0-based) each slot belongs to. */
  footOf: number[];
  /** Added to a line's cost for using this variant. */
  penalty: number;
}

const FOOT_PATTERN: Record<FootId, boolean[]> = {
  iamb: [false, true],
  trochee: [true, false],
  anapest: [false, false, true],
  dactyl: [true, false, false],
};

function build(
  foot: FootId,
  feet: number,
  variant: Variant,
  trim: { head: number; tail: number; extraWeak: boolean },
  penalty: number
): Template {
  const unit = FOOT_PATTERN[foot];
  let slots: boolean[] = [];
  let footOf: number[] = [];
  for (let f = 0; f < feet; f += 1) {
    slots = slots.concat(unit);
    footOf = footOf.concat(unit.map(() => f));
  }
  slots = slots.slice(trim.head, slots.length - trim.tail);
  footOf = footOf.slice(trim.head, footOf.length - trim.tail);
  if (trim.extraWeak) {
    slots.push(false);
    footOf.push(feet - 1);
  }
  return { foot, feet, variant, slots, footOf, penalty };
}

const NONE = { head: 0, tail: 0, extraWeak: false };

/** Every template for a metre: the regular line and the variations that keep it. */
export function templatesFor(foot: FootId, feet: number): Template[] {
  const regular = build(foot, feet, "regular", NONE, 0);
  switch (foot) {
    case "iamb":
      return [
        regular,
        build(foot, feet, "feminine", { ...NONE, extraWeak: true }, 0),
      ];
    case "trochee":
      return [
        regular,
        build(foot, feet, "catalectic", { ...NONE, tail: 1 }, 0),
      ];
    case "anapest":
      return [
        regular,
        build(foot, feet, "feminine", { ...NONE, extraWeak: true }, 0),
        build(foot, feet, "headless", { ...NONE, head: 1 }, 0),
        build(foot, feet, "headless", { ...NONE, head: 2 }, 0.4),
      ];
    default:
      return [
        regular,
        build(foot, feet, "catalectic", { ...NONE, tail: 1 }, 0),
        build(foot, feet, "truncated", { ...NONE, tail: 2 }, 0),
      ];
  }
}

/* -------------------------------------------------------------- alignment */

export interface Alignment {
  cost: number;
  /** For each template slot, the index of the syllable in it, or null if missing. */
  slotSyllable: (number | null)[];
  /** Syllables that fit no slot. */
  extras: number[];
}

function skipSyllable(cls: SylClass): number {
  return cls === "S" || cls === "M" ? SKIP_OTHER_SYLLABLE : SKIP_WEAK_SYLLABLE;
}

type Step = "match" | "extra" | "missing";

interface Grid {
  cost: number[][];
  step: Step[][];
}

function matchCost(cls: SylClass, strong: boolean): number {
  const c = COST[cls];
  return strong ? c.strong : c.weak;
}

/** The cheapest way into cell (i, j) and the step that gets there. */
function cheapest(
  grid: Grid,
  syls: readonly Syl[],
  slots: readonly boolean[],
  i: number,
  j: number
): [number, Step] {
  let best = Number.POSITIVE_INFINITY;
  let how: Step = "match";
  if (i > 0 && j > 0) {
    best = grid.cost[i - 1][j - 1] + matchCost(syls[i - 1].cls, slots[j - 1]);
  }
  if (i > 0) {
    const value = grid.cost[i - 1][j] + skipSyllable(syls[i - 1].cls);
    if (value < best) {
      best = value;
      how = "extra";
    }
  }
  if (j > 0) {
    const value = grid.cost[i][j - 1] + SKIP_SLOT;
    if (value < best) {
      best = value;
      how = "missing";
    }
  }
  return [best, how];
}

function fillGrid(syls: readonly Syl[], slots: readonly boolean[]): Grid {
  const n = syls.length;
  const m = slots.length;
  const grid: Grid = {
    cost: Array.from({ length: n + 1 }, () =>
      new Array<number>(m + 1).fill(Number.POSITIVE_INFINITY)
    ),
    step: Array.from({ length: n + 1 }, () =>
      new Array<Step>(m + 1).fill("match")
    ),
  };
  grid.cost[0][0] = 0;
  for (let i = 0; i <= n; i += 1) {
    for (let j = 0; j <= m; j += 1) {
      if (i > 0 || j > 0) {
        [grid.cost[i][j], grid.step[i][j]] = cheapest(grid, syls, slots, i, j);
      }
    }
  }
  return grid;
}

/** Align syllables to slots at least cost. */
export function align(
  syls: readonly Syl[],
  slots: readonly boolean[]
): Alignment {
  const grid = fillGrid(syls, slots);
  const slotSyllable: (number | null)[] = new Array<number | null>(
    slots.length
  ).fill(null);
  const extras: number[] = [];
  let i = syls.length;
  let j = slots.length;
  while (i > 0 || j > 0) {
    const how = grid.step[i][j];
    if (how === "match") {
      slotSyllable[j - 1] = i - 1;
      i -= 1;
      j -= 1;
    } else if (how === "extra") {
      extras.push(i - 1);
      i -= 1;
    } else {
      j -= 1;
    }
  }
  return {
    cost: grid.cost[syls.length][slots.length],
    slotSyllable,
    extras: extras.reverse(),
  };
}

export interface TemplateFit {
  template: Template;
  alignment: Alignment;
  /** Alignment cost plus the template's penalty. */
  cost: number;
}

/** The best template of one metre for a line. */
export function bestFit(
  syls: readonly Syl[],
  foot: FootId,
  feet: number
): TemplateFit {
  let best: TemplateFit | null = null;
  for (const template of templatesFor(foot, feet)) {
    const alignment = align(syls, template.slots);
    const cost = alignment.cost + template.penalty;
    if (!best || cost < best.cost) {
      best = { template, alignment, cost };
    }
  }
  return best as TemplateFit;
}

/* ----------------------------------------------------------- poem metres */

export interface MeterChoice {
  foot?: FootId;
  feet?: number;
}

export interface FootRanking {
  foot: FootId;
  /** Total cost over the poem, each line at its best length. */
  cost: number;
  /** Cost per syllable — lower is a closer fit. */
  perSyllable: number;
  /** Each line's best length in feet. */
  feetByLine: number[];
}

const TIE = 0.5;

function mode(values: readonly number[]): number {
  const counts = new Map<number, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  let best = values[0] ?? 0;
  let bestCount = 0;
  for (const value of values) {
    const count = counts.get(value) ?? 0;
    if (count > bestCount) {
      best = value;
      bestCount = count;
    }
  }
  return best;
}

/** The line lengths worth trying: all of them, or the one the reader fixed. */
function lengthsToTry(choice: MeterChoice, foot: FootId): number[] {
  return choice.feet
    ? [choice.feet]
    : Array.from({ length: maxFeetFor(foot) }, (_, k) => k + 1);
}

/** Every foot type ranked by how well the poem's lines fit it at their best lengths. */
export function rankFeet(
  lines: readonly (readonly Syl[])[],
  choice: MeterChoice = {}
): FootRanking[] {
  const total = lines.reduce((sum, line) => sum + line.length, 0);
  const out: FootRanking[] = [];
  for (const foot of choice.foot ? [choice.foot] : FOOT_IDS) {
    const lengths = lengthsToTry(choice, foot);
    const costs = lines.map((line) =>
      lengths.map((feet) => ({ feet, cost: bestFit(line, foot, feet).cost }))
    );
    const firstPass = costs.map(
      (row) => row.reduce((a, b) => (b.cost < a.cost ? b : a)).feet
    );
    const common = mode(firstPass);
    // Within a whisker of the best, prefer the poem's usual length.
    const feetByLine = costs.map((row) => {
      const best = Math.min(...row.map((entry) => entry.cost));
      const usual = row.find((entry) => entry.feet === common);
      if (usual && usual.cost <= best + TIE) {
        return common;
      }
      return row.reduce((a, b) => (b.cost < a.cost ? b : a)).feet;
    });
    const cost = costs.reduce(
      (sum, row, i) =>
        sum + (row.find((entry) => entry.feet === feetByLine[i])?.cost ?? 0),
      0
    );
    out.push({ foot, cost, perSyllable: total ? cost / total : 0, feetByLine });
  }
  const order = (foot: FootId) => FOOT_IDS.indexOf(foot);
  return out.sort(
    (a, b) => a.perSyllable - b.perSyllable || order(a.foot) - order(b.foot)
  );
}

/* --------------------------------------------------------------- feet */

export type FootName =
  | "iamb"
  | "trochee"
  | "spondee"
  | "pyrrhic"
  | "anapest"
  | "dactyl"
  | "amphibrach"
  | "stress"
  | "unstressed"
  | "other";

const FOOT_NAMES: Record<string, FootName> = {
  wS: "iamb",
  Sw: "trochee",
  SS: "spondee",
  ww: "pyrrhic",
  wwS: "anapest",
  Sww: "dactyl",
  wSw: "amphibrach",
  S: "stress",
  w: "unstressed",
};

/** What a syllable counts as once its slot is known. */
function effective(
  cls: SylClass,
  strong: boolean,
  threeSyllableFeet: boolean
): "S" | "w" {
  // A stressed one-syllable word in a weak slot of a three-syllable foot reads as
  // demoted: "HALF a LEAGUE" is a dactyl, not S-w-S. In two-syllable feet it makes a
  // spondee, the usual name for "Rough winds".
  if (cls === "M" && !strong && threeSyllableFeet) {
    return "w";
  }
  if (cls === "S" || cls === "M") {
    return "S";
  }
  if (cls === "w") {
    return "w";
  }
  return strong ? "S" : "w";
}

export interface ScannedFoot {
  name: FootName;
  /** "wS", "Sw"… with extras included. */
  pattern: string;
  /** True when the foot is not the one the metre calls for. */
  substituted: boolean;
}

export function scanFeet(
  syls: readonly Syl[],
  fit: TemplateFit,
  foot: FootId
): ScannedFoot[] {
  const { template, alignment } = fit;
  const three = FOOT_PATTERN[foot].length === 3;
  const patterns: string[] = Array.from({ length: template.feet }, () => "");
  const expected: string[] = Array.from({ length: template.feet }, () => "");
  for (const [slot, strong] of template.slots.entries()) {
    const foot = template.footOf[slot];
    expected[foot] += strong ? "S" : "w";
    const at = alignment.slotSyllable[slot];
    if (at !== null) {
      patterns[foot] += effective(syls[at].cls, strong, three);
    }
  }
  // Extra syllables join the foot of the syllable before them.
  for (const extra of alignment.extras) {
    let foot = 0;
    for (let slot = 0; slot < template.slots.length; slot += 1) {
      const at = alignment.slotSyllable[slot];
      if (at !== null && at < extra) {
        foot = template.footOf[slot];
      }
    }
    patterns[foot] += effective(syls[extra].cls, false, three);
  }
  return patterns.map((pattern, foot) => ({
    name: FOOT_NAMES[pattern] ?? "other",
    pattern,
    substituted: pattern !== expected[foot],
  }));
}

/* --------------------------------------------------------------- the poem */

export type FitLabel = "regular" | "substitutions" | "loose" | "irregular";
export type Confidence = "high" | "medium" | "low";

export function fitLabel(fit: number): FitLabel {
  if (fit >= 0.9) {
    return "regular";
  }
  if (fit >= 0.7) {
    return "substitutions";
  }
  return fit >= 0.5 ? "loose" : "irregular";
}

export interface ScannedLine {
  line: LineInfo;
  readings: Reading[];
  /** Words read other than as the dictionary has them. */
  alternates: { word: WordInfo; reading: Reading }[];
  syls: Syl[];
  fit: TemplateFit;
  feet: ScannedFoot[];
  /** 0–1: 1 − cost ÷ syllables. */
  score: number;
  label: FitLabel;
  confidence: Confidence;
  /** The closest other foot type, when it is nearly as good. */
  rival: { foot: FootId; feet: number; cost: number } | null;
}

const ALTERNATE_PENALTY = 0.2;
const MAX_AMBIGUOUS = 6;

function defaultReadings(words: readonly WordInfo[]): Reading[] {
  return words.map((word) => word.reading);
}

/** Every combination of readings for a line's ambiguous words (capped). */
function readingCombinations(words: readonly WordInfo[]): Reading[][] {
  let combos: Reading[][] = [[]];
  let ambiguous = 0;
  for (const word of words) {
    const options =
      word.alternatives.length > 0 && ambiguous < MAX_AMBIGUOUS
        ? [word.reading, ...word.alternatives]
        : [word.reading];
    if (options.length > 1) {
      ambiguous += 1;
    }
    combos = combos.flatMap((combo) =>
      options.map((reading) => [...combo, reading])
    );
  }
  return combos;
}

function alternatesUsed(
  words: readonly WordInfo[],
  readings: readonly Reading[]
) {
  return words.flatMap((word, i) =>
    readings[i] === word.reading ? [] : [{ word, reading: readings[i] }]
  );
}

function rivalFor(
  syls: readonly Syl[],
  foot: FootId,
  best: number
): ScannedLine["rival"] {
  let rival: ScannedLine["rival"] = null;
  for (const other of FOOT_IDS) {
    if (other === foot) {
      continue;
    }
    for (let feet = 1; feet <= maxFeetFor(other); feet += 1) {
      const cost = bestFit(syls, other, feet).cost;
      if (!rival || cost < rival.cost) {
        rival = { foot: other, feet, cost };
      }
    }
  }
  return rival && rival.cost - best < 1 ? rival : null;
}

function confidenceOf(score: number, margin: number): Confidence {
  if (score >= 0.8 && margin >= 0.75) {
    return "high";
  }
  return score < 0.6 || margin < 0.3 ? "low" : "medium";
}

/** Scan one line against a metre, choosing the best reading of ambiguous words. */
export function scanLine(
  line: LineInfo,
  foot: FootId,
  feet: number,
  allowAlternates: boolean
): ScannedLine {
  const combos = allowAlternates
    ? readingCombinations(line.words)
    : [defaultReadings(line.words)];
  let chosen = combos[0];
  let chosenSyls = syllablesOf(line.words, chosen);
  let chosenFit = bestFit(chosenSyls, foot, feet);
  let chosenTotal = chosenFit.cost;
  for (const combo of combos.slice(1)) {
    const used = alternatesUsed(line.words, combo).length;
    const syls = syllablesOf(line.words, combo);
    const fit = bestFit(syls, foot, feet);
    const total = fit.cost + used * ALTERNATE_PENALTY;
    if (total < chosenTotal) {
      chosen = combo;
      chosenSyls = syls;
      chosenFit = fit;
      chosenTotal = total;
    }
  }
  const count = Math.max(chosenSyls.length, 1);
  const score = Math.max(0, 1 - chosenFit.cost / count);
  const rival = rivalFor(chosenSyls, foot, chosenFit.cost);
  const margin = rival ? rival.cost - chosenFit.cost : 2;
  return {
    line,
    readings: chosen,
    alternates: alternatesUsed(line.words, chosen),
    syls: chosenSyls,
    fit: chosenFit,
    feet: scanFeet(chosenSyls, chosenFit, foot),
    score,
    label: fitLabel(score),
    confidence: confidenceOf(score, margin),
    rival,
  };
}

export type Regularity = "regular" | "loose" | "none";

export type LengthPattern = "uniform" | "alternating" | "varied";

export interface PoemScansion {
  foot: FootId;
  /** The usual line length in feet. */
  feet: number;
  /** How the line lengths run: all alike, a repeating pair (4, 3, 4, 3), or mixed. */
  pattern: LengthPattern;
  /** The two lengths of an alternating pattern, longer first. */
  alternating: [number, number] | null;
  lengths: number[];
  /** Every foot type, closest fit first. */
  ranking: FootRanking[];
  lines: ScannedLine[];
  /** Mean line fit, 0–1. */
  score: number;
  /** How closely the poem keeps its best metre. */
  regularity: Regularity;
  /** Share of syllables whose stress the dictionary fixes (not flexible or unknown). */
  fixedShare: number;
  /** How clearly the best foot beats the best of another foot type. */
  confidence: Confidence;
  /** Lines that fit with at most a few substitutions. */
  fitting: number;
}

const REGULAR_SCORE = 0.85;
const LOOSE_SCORE = 0.7;

function regularityOf(score: number): Regularity {
  if (score >= REGULAR_SCORE) {
    return "regular";
  }
  return score >= LOOSE_SCORE ? "loose" : "none";
}

function fixedShareOf(lines: readonly ScannedLine[]): number {
  let fixed = 0;
  let total = 0;
  for (const line of lines) {
    for (const syl of line.syls) {
      total += 1;
      if (syl.cls === "S" || syl.cls === "w") {
        fixed += 1;
      }
    }
  }
  return total ? fixed / total : 0;
}

function hasSyllables(word: WordInfo): boolean {
  return word.reading.syllables > 0;
}

const UNIFORM_SHARE = 0.75;

function lengthPattern(lengths: readonly number[]): {
  pattern: LengthPattern;
  alternating: [number, number] | null;
} {
  const usual = mode(lengths);
  if (
    lengths.filter((n) => n === usual).length / lengths.length >=
    UNIFORM_SHARE
  ) {
    return { pattern: "uniform", alternating: null };
  }
  const a = mode(lengths.filter((_, i) => i % 2 === 0));
  const b = mode(lengths.filter((_, i) => i % 2 === 1));
  const fitting = lengths.filter((n, i) => n === (i % 2 === 0 ? a : b)).length;
  if (
    lengths.length >= 4 &&
    a !== b &&
    fitting / lengths.length >= UNIFORM_SHARE
  ) {
    return {
      pattern: "alternating",
      alternating: [Math.max(a, b), Math.min(a, b)],
    };
  }
  return { pattern: "varied", alternating: null };
}

function poemConfidence(
  score: number,
  gapPerLine: number,
  lines: number
): Confidence {
  if (lines < 2) {
    return "low";
  }
  if (score >= 0.8 && gapPerLine >= 0.3) {
    return "high";
  }
  return score < 0.6 || gapPerLine < 0.1 ? "low" : "medium";
}

/** Find the dominant metre of a poem and scan every line against it. */
export function scanPoem(
  lines: readonly LineInfo[],
  choice: MeterChoice,
  allowAlternates: boolean
): PoemScansion | null {
  const usable = lines.filter((line) => line.words.some(hasSyllables));
  if (usable.length === 0) {
    return null;
  }
  const defaults = usable.map((line) =>
    syllablesOf(line.words, defaultReadings(line.words))
  );
  const ranking = rankFeet(defaults, choice);
  const best = ranking[0];
  const scanned = usable.map((line, i) =>
    scanLine(line, best.foot, best.feetByLine[i], allowAlternates)
  );
  const score =
    scanned.reduce((sum, line) => sum + line.score, 0) / scanned.length;
  const other = ranking.find((entry) => entry.foot !== best.foot);
  const gapPerLine = other ? (other.cost - best.cost) / usable.length : 1;
  const lengths = scanned.map((line) => line.fit.template.feet);
  return {
    foot: best.foot,
    feet: mode(lengths),
    ...lengthPattern(lengths),
    lengths,
    ranking,
    lines: scanned,
    score,
    regularity: regularityOf(score),
    fixedShare: fixedShareOf(scanned),
    confidence: poemConfidence(score, gapPerLine, usable.length),
    fitting: scanned.filter((line) => line.score >= 0.7).length,
  };
}
